import React, { useState } from 'react';
import { Info } from '@icon-park/react';
import { uploadFile } from '../../api/upload';
import './styles/AttachmentEditor.css';

function AttachmentEditor({ data, onUpdate, labelId }) {
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const handleAddAttachment = () => {
    const newAttachments = [
      ...(data.attachments || []),
      { name: '', file: null }
    ];
    onUpdate({ ...data, attachments: newAttachments });
  };

  const handleUpdateName = (index, value) => {
    const newAttachments = [...data.attachments];
    newAttachments[index].name = value;
    onUpdate({ ...data, attachments: newAttachments });
  };

  const handleFileUpload = async (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 验证文件大小（最大10MB）
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setUploadError(`文件大小超过限制（最大${(maxSize / 1024 / 1024).toFixed(0)}MB）`);
      return;
    }

    // 验证文件类型
    const allowedTypes = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt'];
    const fileName = file.name.toLowerCase();
    const isValidType = allowedTypes.some(ext => fileName.endsWith(ext));
    if (!isValidType) {
      setUploadError(`不支持的文件格式，请上传：${allowedTypes.join(', ')}`);
      return;
    }

    setUploadingIndex(index);
    setUploadError(null);

    try {
      // 调用文件上传API
      const fileUrl = await uploadFile(file, {
        fileType: 'attachment',
        labelId: labelId || undefined, // 如果有标签ID则传递
        onProgress: (progress) => {
          // 可以在这里显示上传进度
          console.log(`上传进度: ${progress}%`);
        }
      });

      // 更新附件数据，包含文件URL
      const newAttachments = [...data.attachments];
      newAttachments[index].file = {
        name: file.name,
        size: file.size,
        url: fileUrl  // ✅ 保存文件URL
      };
      onUpdate({ ...data, attachments: newAttachments });
    } catch (error) {
      console.error('文件上传失败:', error);
      setUploadError(error.message || '文件上传失败，请稍后重试');
      // 清空文件选择，允许重新选择
      e.target.value = '';
    } finally {
      setUploadingIndex(null);
    }
  };

  const handleDeleteFile = (index) => {
    const newAttachments = [...data.attachments];
    newAttachments[index].file = null;
    onUpdate({ ...data, attachments: newAttachments });
  };

  const handleDeleteRow = (index) => {
    const newAttachments = data.attachments.filter((_, i) => i !== index);
    onUpdate({ ...data, attachments: newAttachments });
  };

  return (
    <div className="attachment-editor-container">
      <div className="uploader-header">
        <span className="uploader-title">附件样式</span>
        <span className="info-icon"><Info theme="outline" size={14} fill="#999" strokeWidth={3} /></span>
      </div>

      {/* 布局与卡片样式 */}
      <div style={{ display: 'grid', gap: 12, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <label>布局：</label>
          <select value={data.layout || 'list'} onChange={(e) => onUpdate({ ...data, layout: e.target.value })}>
            <option value="list">列表</option>
            <option value="card">卡片</option>
          </select>
          <label>卡片背景</label>
          <input type="color" value={(data.cardStyle && data.cardStyle.bgColor) || '#f8f9fa'} onChange={(e) => onUpdate({ ...data, cardStyle: { ...(data.cardStyle || {}), bgColor: e.target.value } })} />
          <label>文字颜色</label>
          <input type="color" value={(data.cardStyle && data.cardStyle.textColor) || '#202124'} onChange={(e) => onUpdate({ ...data, cardStyle: { ...(data.cardStyle || {}), textColor: e.target.value } })} />
          <label>边框色</label>
          <input type="color" value={(data.cardStyle && data.cardStyle.borderColor) || '#e8eaed'} onChange={(e) => onUpdate({ ...data, cardStyle: { ...(data.cardStyle || {}), borderColor: e.target.value } })} />
          <label>边框宽(px)</label>
          <input type="number" min="0" max="8" value={(data.cardStyle && data.cardStyle.borderWidth) || 1} onChange={(e) => onUpdate({ ...data, cardStyle: { ...(data.cardStyle || {}), borderWidth: Number(e.target.value) } })} />
          <label>圆角(px)</label>
          <input type="number" min="0" max="24" value={(data.cardStyle && data.cardStyle.radius) || 8} onChange={(e) => onUpdate({ ...data, cardStyle: { ...(data.cardStyle || {}), radius: Number(e.target.value) } })} />
          <label><input type="checkbox" checked={!(data.cardStyle && data.cardStyle.shadow === false)} onChange={(e) => onUpdate({ ...data, cardStyle: { ...(data.cardStyle || {}), shadow: e.target.checked } })} /> 阴影</label>
        </div>
      </div>
      
      <h3 className="section-title">附件</h3>
      
      <div className="attachment-table">
        <div className="table-header-attach">
          <div className="col-name-attach">名称</div>
          <div className="col-file-attach">文件</div>
          <div className="col-action-attach">操作</div>
        </div>
        
        <div className="table-body-attach">
          {data.attachments && data.attachments.map((attachment, index) => (
            <div key={index} className="table-row-attach">
              <div className="col-name-attach">
                <input
                  type="text"
                  value={attachment.name}
                  onChange={(e) => handleUpdateName(index, e.target.value)}
                  placeholder="附件名称"
                  className="table-input-attach"
                />
              </div>
              <div className="col-file-attach">
                {attachment.file ? (
                  <div className="file-info">
                    <span className="file-name">{attachment.file.name}</span>
                    {attachment.file.url && (
                      <span className="file-url-hint" title="文件已上传">✓</span>
                    )}
                  </div>
                ) : (
                  <>
                    <input
                      type="file"
                      id={`file-upload-${index}`}
                      onChange={(e) => handleFileUpload(index, e)}
                      disabled={uploadingIndex === index}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                      style={{ display: 'none' }}
                    />
                    <label 
                      htmlFor={`file-upload-${index}`} 
                      className={`upload-file-link ${uploadingIndex === index ? 'uploading' : ''}`}
                    >
                      {uploadingIndex === index ? '上传中...' : '上传附件'}
                    </label>
                  </>
                )}
              </div>
              <div className="col-action-attach">
                {attachment.file ? (
                  <button 
                    className="action-link delete-link"
                    onClick={() => handleDeleteFile(index)}
                  >
                    删除附件
                  </button>
                ) : (
                  <button 
                    className="action-link delete-link"
                    onClick={() => handleDeleteRow(index)}
                  >
                    删除行
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        
        <div className="table-footer-attach">
          <button className="add-attachment-btn" onClick={handleAddAttachment}>
            增加
          </button>
        </div>
      </div>
      
      {uploadError && (
        <div className="upload-error-message" style={{ 
          color: '#ff4d4f', 
          fontSize: '12px', 
          marginTop: '8px',
          padding: '4px 8px',
          backgroundColor: '#fff2f0',
          borderRadius: '4px'
        }}>
          {uploadError}
        </div>
      )}
    </div>
  );
}

export default AttachmentEditor;
